import React from 'react';
import { Link } from 'react-router-dom';
import ArticleShell from './ArticleShell';
import { H2, H3, P, Callout, CheckList, BulletList } from './academyPrimitives';
import { getGuideBySlug } from '../../data/knowledgeAcademy';

const SERVICE_LINK = (
  <Link to="/e-invoicing-uae" className="text-teal-700 font-semibold underline underline-offset-2 hover:text-teal-900" data-testid="einv-guide-service-link">
    E-Invoicing Compliance &amp; Integration Services
  </Link>
);

const PHASES = [
  ['Pilot programme', 'Selected businesses by written agreement with the Ministry', '—', 'From 1 July 2026'],
  ['Voluntary adoption', 'Any person meeting the technical requirements', '—', 'From 1 July 2026'],
  ['Phase 1', 'Persons with revenue of AED 50 million or more', '30 October 2026', '1 January 2027'],
  ['Phase 2', 'Persons with revenue below AED 50 million', '31 March 2027', '1 July 2027'],
  ['Government entities', 'Federal and local government entities', '31 March 2027', '1 October 2027']
];

const CORNERS = [
  ['Corner 1', 'Supplier', 'Prepares the invoice data in its accounting system or ERP.'],
  ['Corner 2', 'Supplier’s ASP', 'Validates and converts the data to PINT AE and sends it over the Peppol network.'],
  ['Corner 3', 'Buyer’s ASP', 'Receives and validates the e-invoice and delivers it to the buyer.'],
  ['Corner 4', 'Buyer', 'Receives the structured invoice into its own system.'],
  ['Corner 5', 'Federal Tax Authority', 'Receives the relevant tax data reported by the ASPs.']
];

const Table = ({ headers, rows, testId }) => (
  <div className="overflow-x-auto my-6 rounded-xl border border-gray-200" data-testid={testId}>
    <table className="w-full min-w-[560px] text-sm text-left">
      <thead className="bg-gray-900 text-white">
        <tr>{headers.map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr>
      </thead>
      <tbody className="divide-y divide-gray-100 bg-white">
        {rows.map((r) => (
          <tr key={r[0]}>{r.map((c, i) => <td key={i} className={`px-4 py-3 align-top ${i === 0 ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>{c}</td>)}</tr>
        ))}
      </tbody>
    </table>
  </div>
);

const UAEEInvoicingGuide = () => {
  const guide = {
    ...getGuideBySlug('uae-e-invoicing-phases-pint-ae-guide'),
    relatedService: { label: 'E-Invoicing Services', path: '/e-invoicing-uae' }
  };

  const toc = [
    { id: 'overview', label: 'Overview' },
    { id: 'what-is', label: 'What Counts as an E-Invoice?' },
    { id: 'legal-framework', label: 'Legal Framework' },
    { id: 'scope', label: 'Who and What Is in Scope' },
    { id: 'phases', label: 'Implementation Phases and Deadlines' },
    { id: 'model', label: 'The Five-Corner Model' },
    { id: 'pint-ae', label: 'What Is PINT AE?' },
    { id: 'tin', label: 'Participant Identifier and TIN' },
    { id: 'penalties', label: 'Penalties for Missing Deadlines' },
    { id: 'roadmap', label: 'A Practical Readiness Roadmap' },
    { id: 'checklist', label: 'Readiness Checklist' },
    { id: 'wpaa-help', label: 'How WPAA Can Help' }
  ];

  return (
    <ArticleShell guide={guide} toc={toc} testId="ka-article-einvoicing" ctaService="e-invoicing">
      <Callout variant="info" title="At a glance">
        UAE e-invoicing becomes mandatory in phases from <strong>1 January 2027</strong>. Businesses
        with revenue of AED 50 million or more go first. Every business in scope must appoint a
        Ministry of Finance–accredited Service Provider (ASP) before its go-live date, and invoices
        must follow the <strong>PINT AE</strong> data standard.
      </Callout>

      <H2 id="overview">Overview</H2>
      <P>
        The UAE is moving from paper and PDF invoices to structured electronic invoices that are
        exchanged between businesses and reported to the Federal Tax Authority (FTA) in close to real
        time. The change affects how invoices are created, sent, received and stored, and it touches
        accounting systems, customer master data, VAT coding and approval processes.
      </P>
      <P>
        This guide explains the implementation phases, the deadlines that apply to each group, how the
        five-corner exchange model works and what PINT AE means for your invoice data. If you are
        planning your own transition, see our {SERVICE_LINK}.
      </P>

      <H2 id="what-is">What Counts as an E-Invoice?</H2>
      <P>
        An e-invoice is structured invoice data issued and exchanged electronically between a supplier
        and buyer, with relevant tax data reported electronically to the FTA. It is designed to be read
        by systems, not just by people.
      </P>
      <Callout variant="warning" title="PDFs are not e-invoices">
        PDFs, Word documents, scanned copies and invoices sent by email alone do not constitute
        structured e-invoices under the UAE framework. A human-readable copy can still be part of your
        workflow, alongside the required structured exchange.
      </Callout>

      <H2 id="legal-framework">Legal Framework</H2>
      <P>The UAE Electronic Invoicing System is set out mainly in two Ministerial Decisions:</P>
      <BulletList items={[
        'Ministerial Decision No. 243 of 2025 on the Electronic Invoicing System, which sets the scope and obligations.',
        'Ministerial Decision No. 244 of 2025 on the Implementation of the Electronic Invoicing System, which sets the phased timeline.'
      ]} />
      <P>
        Both decisions have since been amended, including by Ministerial Decision No. 197 of 2026, and
        the Ministry of Finance has published detailed UAE Electronic Invoicing Guidelines. Because the
        rules are still being refined, businesses should check the latest official versions before
        finalising their plans.
      </P>

      <H2 id="scope">Who and What Is in Scope</H2>
      <P>
        The system applies to persons conducting business in the UAE and focuses on business-to-business
        (B2B) and business-to-government (B2G) transactions. Business-to-consumer (B2C) transactions are
        currently outside the mandatory scope.
      </P>
      <P>Certain transactions are excluded, some of them only for a transitional period. These include:</P>
      <BulletList items={[
        'Activities of government entities acting in a sovereign capacity.',
        'International passenger transport issued through electronic tickets, and related airline ancillary services.',
        'Certain international transport of goods, for a transitional period.',
        'Certain VAT-exempt or zero-rated financial services.',
        'Transactions between members of the same VAT tax group, for a transitional period.'
      ]} />
      <P>
        Exclusions are tightly defined. Map each of your transaction types against the current decisions
        rather than assuming a whole business line is out of scope.
      </P>

      <H2 id="phases">Implementation Phases and Deadlines</H2>
      <P>
        The rollout is phased by revenue and entity type. Each group has two dates: a deadline to
        appoint an ASP and a go-live date from which e-invoicing is mandatory.
      </P>
      <Table
        testId="einv-guide-phases-table"
        headers={['Phase', 'Who', 'Appoint an ASP by', 'Mandatory from']}
        rows={PHASES}
      />
      <Callout variant="warning" title="Phase 1 deadline has passed">
        The ASP appointment deadline for businesses with revenue of AED 50 million or more was extended
        from 31 July 2026 to <strong>30 October 2026</strong>. The go-live date of 1 January 2027 did
        not change. If your business is in Phase 1 and has not appointed an ASP, act now.
      </Callout>
      <P>
        Revenue thresholds and the way revenue is measured are defined in the decisions. Confirm which
        phase applies to each legal entity in your group, as entities may fall into different phases.
      </P>

      <H2 id="model">The Five-Corner Model</H2>
      <P>
        The UAE uses a Decentralized Continuous Transaction Control and Exchange (DCTCE) model built on
        the Peppol interoperability framework. Invoices do not go through a single government portal;
        they travel between accredited service providers, which also report tax data to the FTA.
      </P>
      <Table testId="einv-guide-corners-table" headers={['Corner', 'Party', 'Role']} rows={CORNERS} />
      <P>
        In practice, your own team stays responsible for the accuracy of the invoice data in Corner 1.
        The ASP validates the format and handles the exchange, but it cannot fix a wrong VAT code or an
        incomplete customer record on your behalf.
      </P>

      <H2 id="pint-ae">What Is PINT AE?</H2>
      <P>
        PINT AE is the UAE specification of the Peppol International (PINT) invoice model. It defines
        which data fields an e-invoice and e-credit note must contain, how they are structured and which
        validation rules apply.
      </P>
      <H3>What PINT AE means for your data</H3>
      <CheckList items={[
        'Supplier and buyer identification must be complete and consistent, including the identifiers used on the network.',
        'Each line needs correct VAT category codes, rates and amounts, including zero-rated, exempt and reverse charge items.',
        'Credit notes, discounts, adjustments and cross-border transactions need to be mapped to the right fields.',
        'Free-text descriptions and manual workarounds that pass on a PDF may fail automatic validation.'
      ]} />
      <P>
        Most of the preparation work is therefore about data quality and VAT classification, not just
        software. Mapping your current invoice templates to PINT AE early shows where the gaps are.
      </P>

      <H2 id="tin">Participant Identifier and TIN</H2>
      <P>
        Each participant on the network has an identifier made up of the prefix <strong>0235</strong>{' '}
        followed by its 10-digit Tax Identification Number (TIN). For VAT-registered businesses the TIN
        is generally the first 10 digits of the Tax Registration Number (TRN). Businesses in scope that
        are not registered for tax may need to register with the FTA to obtain a TIN.
      </P>
      <P>
        Onboarding to the system is initiated through the EmaraTax platform, together with your
        appointed ASP.
      </P>

      <H2 id="penalties">Penalties for Missing Deadlines</H2>
      <P>
        The FTA has stated that an administrative penalty of <strong>AED 5,000 for each month or part of
        a month</strong> applies for delay in implementing the e-invoicing system, including failure to
        appoint an ASP by the applicable deadline. Further penalties can apply to other e-invoicing
        failures under the penalty rules.
      </P>

      <H2 id="roadmap">A Practical Readiness Roadmap</H2>
      <H3>1. Confirm your phase and scope</H3>
      <P>Identify which entities are in scope, which phase each falls into and which transaction types are excluded.</P>
      <H3>2. Assess systems and data</H3>
      <P>Review your accounting system or ERP, its integration options and the quality of customer, supplier and product data.</P>
      <H3>3. Select and appoint an ASP</H3>
      <P>Compare providers on the official Ministry of Finance list for integration fit, volumes, support and cost, then appoint before your deadline.</P>
      <H3>4. Map invoice data to PINT AE</H3>
      <P>Map fields and VAT codes, including credit notes and special cases, and fix gaps at source.</P>
      <H3>5. Test, train and go live</H3>
      <P>Test sales invoices, supplier invoices and credit notes, train your teams on rejections and exceptions, and plan go-live checks.</P>
      <H3>6. Reconcile after go-live</H3>
      <P>Reconcile e-invoice records with the ledger and VAT returns, and review recurring validation issues.</P>

      <H2 id="checklist">Readiness Checklist</H2>
      <CheckList items={[
        'Phase and ASP deadline confirmed for each legal entity',
        'TIN available for each entity in scope',
        'Accredited Service Provider selected from the official list and appointed',
        'Accounting system or ERP integration route agreed',
        'Customer and supplier master data cleaned and complete',
        'VAT categories and tax codes mapped to PINT AE',
        'Credit note, discount and cross-border scenarios tested',
        'Process agreed for rejected invoices and corrections',
        'Staff in billing, purchasing and accounting trained',
        'Reconciliation of e-invoices to ledgers and VAT returns planned'
      ]} />

      <H2 id="wpaa-help">How WPAA Can Help</H2>
      <P>
        WPAA is not an Accredited Service Provider. We support the accounting, tax and coordination side
        of your transition, while your ASP delivers the technical exchange. Our {SERVICE_LINK} cover:
      </P>
      <CheckList items={[
        'Readiness assessment and gap analysis',
        'ASP selection and onboarding support',
        'ERP and accounting system integration coordination',
        'Invoice data mapping and VAT review, including PINT AE alignment',
        'Staff training and process documentation',
        'Ongoing compliance and reconciliation support'
      ]} />
      <Callout variant="info" title="Plan your transition">
        Book an E-Invoicing Readiness Consultation and we will help you confirm your phase, deadlines and
        the work needed before go-live.
      </Callout>

      <H2>Official References</H2>
      <BulletList items={[
        'Ministerial Decision No. 243 of 2025 on the Electronic Invoicing System.',
        'Ministerial Decision No. 244 of 2025 on the Implementation of the Electronic Invoicing System.',
        'Ministerial Decision No. 197 of 2026 amending certain provisions of Ministerial Decision No. 243 of 2025.',
        'Ministry of Finance — UAE Electronic Invoicing Guidelines (Version 1.0, February 2026).',
        'Federal Tax Authority — announcement on e-invoicing deadlines and administrative penalties.'
      ]} />
      <P className="text-xs text-gray-500 italic mt-6">Last reviewed: October 2026.</P>

      <Callout variant="warning" title="Disclaimer">
        This article is intended for general informational purposes only and does not constitute tax or
        legal advice. E-invoicing rules, deadlines and exclusions may change. Businesses should confirm
        their obligations against the latest official publications and obtain professional advice.
      </Callout>
    </ArticleShell>
  );
};

export default UAEEInvoicingGuide;
